import { InfiniteData, QueryKey, useInfiniteQuery } from '@tanstack/react-query'
import { useEffect } from 'react'

import {
  EstablishmentsArgs,
  SearchAccreditedEstablishmentsListResponse,
  searchEstablishmentsList
} from '@/modules/accredited-establishments/api'
import { addError } from '@/global/plugins/observability'
import {
  AccreditedEstablishmentsCode,
  Context
} from '@/global/plugins/observability/observability.constants'
import { makeSearchEstablishmentsListQueryKey } from '@/modules/accredited-establishments/functions/make-search-establishments-query-key'
import {
  DEFAULT_PAGINATION_LIMIT,
  DEFAULT_PAGINATION_OFFSET
} from '@/modules/accredited-establishments/constants/accredited-establishments'
import { ApiErrorResponse } from '@/global/api/types'
import { AxiosError } from 'axios'
import { toastApiError } from '@/global/utils/toastApiError/toastApiError'
import { getNextPageParam } from '@/modules/accredited-establishments/functions'

type UseSearchEstablishmentsListProps = EstablishmentsArgs & {
  enabled?: boolean
  toastOnError?: boolean
}

const useSearchEstablishmentsList = ({
  enabled,
  toastOnError = true,
  ...rest
}: UseSearchEstablishmentsListProps) => {
  const result = useInfiniteQuery<
    SearchAccreditedEstablishmentsListResponse,
    AxiosError<ApiErrorResponse>,
    InfiniteData<SearchAccreditedEstablishmentsListResponse, [number, number]>,
    QueryKey,
    [number, number]
  >({
    queryKey: makeSearchEstablishmentsListQueryKey(rest),
    queryFn: ({ pageParam }) =>
      searchEstablishmentsList({
        ...rest,
        offset: pageParam[0],
        limit: pageParam[1]
      }),
    enabled,
    retry: false,
    initialPageParam: [DEFAULT_PAGINATION_OFFSET, DEFAULT_PAGINATION_LIMIT],
    getNextPageParam: (lastPage, allPages) => {
      return getNextPageParam({
        totalOfItems: lastPage.totalItems,
        totalOfCurrentPages: allPages?.length || 0
      })
    }
  })

  const { error } = result

  useEffect(() => {
    if (error && toastOnError) {
      const data = {
        context: Context.ACCREDITED_ESTABLISHMENTS,
        code: AccreditedEstablishmentsCode.GET_ACCREDITED_ESTABLISHMENTS_LIST_ERROR,
        message:
          'Erro ao obter os estabelecimentos da listagem com os filtros selecionados',
        ...rest
      }
      addError({ error, data })

      toastApiError({ status: error.response?.status })
    }
  }, [error, rest])

  return result
}

export { useSearchEstablishmentsList }
export type { UseSearchEstablishmentsListProps as UseSearchEstablishmentsProps }
